import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

type FieldShellProps = {
  label: string
  hint?: string
  error?: string | null
  children: ReactNode
  id: string
}

export function FieldShell({ label, hint, error, children, id }: FieldShellProps) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  )
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string
  error?: string | null
}

export function Input({ label, hint, error, id, className, ...rest }: InputProps) {
  const inputId = id ?? rest.name ?? `input-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} id={inputId}>
      <input id={inputId} className={['input', className].filter(Boolean).join(' ')} {...rest} />
    </FieldShell>
  )
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string
  hint?: string
  error?: string | null
}

export function Textarea({ label, hint, error, id, className, ...rest }: TextareaProps) {
  const inputId = id ?? rest.name ?? `ta-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} id={inputId}>
      <textarea id={inputId} className={['textarea', className].filter(Boolean).join(' ')} {...rest} />
    </FieldShell>
  )
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string
  hint?: string
  error?: string | null
  children: ReactNode
}

export function Select({ label, hint, error, id, className, children, ...rest }: SelectProps) {
  const inputId = id ?? rest.name ?? `sel-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <FieldShell label={label} hint={hint} error={error ?? null} id={inputId}>
      <select id={inputId} className={['select', className].filter(Boolean).join(' ')} {...rest}>
        {children}
      </select>
    </FieldShell>
  )
}
